import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import toast from "react-hot-toast";
import {
  getUserAccountInfo,
  updateUserAccountInfo,
} from "../../../Services/UserService";
import { MESSAGES } from "../constant";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import * as route from "../../../routes";
import { Loader } from "../../../Components/Common";
import { displayValue, formatDateDDMMYYYY, toProperCase } from "../../../Utils";
import { YES_NO_OPTIONS } from "./constants";
import { getReferrals } from "../../../Services/essServices";
import { REFERRAL_STATUS_LABELS } from "../../ESS/constants";
import * as Yup from "yup";


const AccountInfoTab = ({
  isUserEditable,
  setIsUserEditable,
  isHRAdminEditable,
  setIsHRAdminEditable,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();

  const isViewMode = location.pathname.includes("view");

  const [loading, setLoading] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [isOnboarded, setIsOnboarded] = useState(false);

  const [referralOptions, setReferralOptions] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  const [initialValues, setInitialValues] = useState({
    id: "",
    resume_received_by: "",
    interviewed_taken_by: "",
    through_agency_name: "",
    offer_letter_given_on: "",
    offer_letter_given_by: "",
    appointment_letter_date: "",
    appointment_letter_given_by: "",
    to_be_joined_on: "",
    form_given_on: "",
    email: "",
    employees_id_card: "",
    diary_handbook: "",
    mobile_sim_card: "",
    access_card: "",
    workplace_desk: "",
    accident_insurance: "",
    mediclaim_insurance: "",
    laptop_pc: "",
    visiting_cards: "",
    email_id_password_given: "",
    company_policy_accepted_on: "",
    referral: "",
  });

const validationSchema = Yup.object().shape({
  referral: Yup.string().nullable(),
  to_be_joined_on: Yup.date()
    .nullable()
    .when("referral", {
      is: (referral) => referral !== null && referral !== "",
      then: (schema) =>
        schema.required("To Be Joined On (DOJ) is required for referrals"),
      otherwise: (schema) => schema,
    }),
});



  const fetchUserAccountInfo = async () => {
    try {
      setLoadingUser(true);

      let u = null;
      const res = await getUserAccountInfo(userId);
      if (res.status === 200) {
        u = res.data;
      } else {
        const msg = extractErrorMessage(res) || MESSAGES.USER_DATA_ERROR;
        toast.error(msg);
      }
      setIsOnboarded((u.employment_status || "PENDING_ONBOARDING") !== "PENDING_ONBOARDING");
      setInitialValues({
        id: u.id || "",
        resume_received_by: u.resume_received_by || "",
        interviewed_taken_by: u.interviewed_taken_by || "",
        through_agency_name: u.through_agency_name || "",
        offer_letter_given_on: u.offer_letter_given_on || "",
        offer_letter_given_by: u.offer_letter_given_by || "",
        appointment_letter_date: u.appointment_letter_date || "",
        appointment_letter_given_by: u.appointment_letter_given_by || "",
        to_be_joined_on: u.to_be_joined_on || "",
        form_given_on: u.form_given_on || "",
        employees_id_card: u.employees_id_card ?? "",
        diary_handbook: u.diary_handbook ?? "",
        mobile_sim_card: u.mobile_sim_card ?? "",
        access_card: u.access_card ?? "",
        workplace_desk: u.workplace_desk ?? "",
        accident_insurance: u.accident_insurance ?? "",
        mediclaim_insurance: u.mediclaim_insurance ?? "",
        laptop_pc: u.laptop_pc ?? "",
        visiting_cards: u.visiting_cards ?? "",
        email_id_password_given: u.email_id_password_given ?? false,
        email: u.email ?? "",
        company_policy_accepted_on: u.company_policy_accepted_on || "",
        referral: u.referral,
        referral_data: u.referral_data,
      });

    } catch (err) {
      toast.error(extractErrorMessage(err) || MESSAGES.USER_DATA_ERROR);
    } finally {
      setLoadingUser(false);
    }
  };

  const fetchReferrals = async () => {
    try {
      setLoadingReferrals(true);
      const res = await getReferrals({is_declaration_signed:true, status:REFERRAL_STATUS_LABELS.SELECTED});
      if (res.status === 200) {
        setReferralOptions(res.data.results);
      }
    } catch (err) {
      toast.error("Failed to load referrals");
    } finally {
      setLoadingReferrals(false);
    }
  };


  useEffect(() => {
    fetchUserAccountInfo();
  }, [userId]);

  useEffect(() => {
      fetchReferrals();
  }, []);


  const handleSubmit = async (values, { setSubmitting }) => {
    if (isViewMode) return;
    setSubmitting(true);
    setLoading(true);

    try {
      const payload = {
        user: userId,
        resume_received_by: values.resume_received_by || "",
        interviewed_taken_by: values.interviewed_taken_by || "",
        through_agency_name: values.through_agency_name || "",
        offer_letter_given_on: values.offer_letter_given_on || null,
        offer_letter_given_by: values.offer_letter_given_by || "",
        appointment_letter_date: values.appointment_letter_date || null,
        appointment_letter_given_by: values.appointment_letter_given_by || null,
        to_be_joined_on: values.to_be_joined_on || null,
        form_given_on: values.form_given_on || null,
        employees_id_card: values.employees_id_card || "",
        diary_handbook: values.diary_handbook || "",
        mobile_sim_card: values.mobile_sim_card || "",
        access_card: values.access_card || "",
        workplace_desk: values.workplace_desk || "",
        accident_insurance: values.accident_insurance || "",
        mediclaim_insurance: values.mediclaim_insurance || "",
        laptop_pc: values.laptop_pc || "",
        visiting_cards: values.visiting_cards || "",
        email_id_password_given: values.email_id_password_given,
        email: values.email || null,
        company_policy_accepted_on: values.company_policy_accepted_on || null,
        referral: values.referral || null,
      };

      const res = await updateUserAccountInfo(initialValues.id, payload);

      if (res.status === 200 || res.status === 204) {
        toast.success(MESSAGES.ACCOUNT_INFO_UPDATE_SUCCESS);
        navigate(route.USER_PAGE);
      } else {
        toast.error(
          extractErrorMessage(res) || MESSAGES.ACCOUNT_INFO_UPDATE_FAILED
        );
      }
    } catch (err) {
      toast.error(MESSAGES.ACCOUNT_INFO_UPDATE_FAILED);
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  if (loadingUser)
    return (
      <div className="flex justify-center pt-10">
        <Loader size={6} />
      </div>
    );

  const inputClass =
    "mt-1 w-[95%] border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary";

  const selectClass = `
  w-[95%]
  rounded-md
  border
  border-gray-300
  bg-white
  px-3
  py-2
  text-sm
  text-gray-900
  shadow-sm
  focus:outline-none
  focus:ring-2
  focus:ring-indigo-500
  focus:border-indigo-500
  disabled:bg-gray-100
  disabled:text-gray-500
  disabled:cursor-not-allowed
`;

  return (
    <div className="p-4">
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue }) => (
          <Form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* LEFT COLUMN */}
              <div className="space-y-6">
                {/* Recruitment information */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Recruitment information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Resume Received By */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Resume Received By
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.resume_received_by)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="resume_received_by"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Resume Received By..."
                          />
                        </>
                      )}
                    </div>

                    {/* Interviewed by */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Interviewed By
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.interviewed_taken_by)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="interviewed_taken_by"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Interview Taken By..."
                          />
                        </>
                      )}
                    </div>

                    {/* Through Whom/Agency */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Through Whom/Agency
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.through_agency_name)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="through_agency_name"
                            type="text"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Through whom/Agency..."
                          />
                        </>
                      )}
                    </div>

                    {/* Referral */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Referred By{" "}
                        <span className="text-gray-400">(Optional)</span>
                      </label>

                      {isViewMode ? (
                        <p className="text-sm text-gray-900">
                          {initialValues.referral_data
                            ? initialValues?.referral_data
                                ?.referred_by_full_name
                            : "Not Referred"}
                        </p>
                      ) : (
                        <>
                          {isOnboarded ? (
                            <select
                              className={`${selectClass} mt-1 `}
                              disabled={isOnboarded}
                            >
                              <option className="">
                                {
                                  initialValues?.referral_data
                                    ?.referred_by_full_name || "Not Referred"
                                }
                              </option>
                            </select>
                          ) : (
                            <Field
                              as="select"
                              name="referral"
                              disabled={isOnboarded}
                              className={selectClass}
                            >
                              <option value="">— Not Referred —</option>

                              {loadingReferrals ? (
                                <option disabled>Loading referrals...</option>
                              ) : (
                                referralOptions.map((ref) => (
                                  <option key={ref.id} value={ref.id}>
                                    {ref.referred_by_full_name}
                                    {" to "} {ref.referred_person_name}
                                    {" - "}({toProperCase(ref.status)})
                                  </option>
                                ))
                              )}
                            </Field>
                          )}

                          {!isOnboarded && (
                            <p className="text-xs text-gray-500">
                              Select only if the employee joined through a
                              referral
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Offer and Appointment */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Offer and Appointment
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Offer letter given on */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Offer Letter Given On
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(
                              initialValues.offer_letter_given_on
                            )
                          )}
                        </p>
                      ) : (
                        <Field
                          name="offer_letter_given_on"
                          type="date"
                          disabled={isOnboarded}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        />
                      )}
                    </div>

                    {/* Offer Letter Given By */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Offer Letter Given By
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.offer_letter_given_by)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="offer_letter_given_by"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Offer letter given by..."
                          />
                        </>
                      )}
                    </div>

                    {/* Appointment Letter Date */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Appointment Letter Date
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(
                              initialValues.appointment_letter_date
                            )
                          )}
                        </p>
                      ) : (
                        <Field
                          name="appointment_letter_date"
                          type="date"
                          disabled={isOnboarded}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        />
                      )}
                    </div>

                    {/* Appointment Letter given by */}
                    <div>
                      <label className="text-sm text-gray-700">
                        Appointment Letter Given By
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            initialValues.appointment_letter_given_by
                          )}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="appointment_letter_given_by"
                            type="text"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Appointment letter given by..."
                          />
                        </>
                      )}
                    </div>

                    {/* To be join on */}
                    <div>
                      <label className="text-sm text-gray-700">
                        To be join on
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(initialValues.to_be_joined_on)
                          )}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="to_be_joined_on"
                            type="date"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          />
                          <ErrorMessage
                            name="to_be_joined_on"
                            component="div"
                            className="text-red-500 text-sm mt-1"
                          />
                        </>
                      )}
                    </div>

                    {/* Form Given On */}
                    <div className="md:col-span-1">
                      <label className="text-sm text-gray-700">
                        Form Given On
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(initialValues.form_given_on)
                          )}
                        </p>
                      ) : (
                        <Field
                          name="form_given_on"
                          type="date"
                          disabled={isOnboarded}
                          className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-6">
                {/* Company Assets / Equipment  */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Company Assets / Equipment Issued
                  </h3>
                  <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-700">
                        Employee ID Card
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.employees_id_card)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="employees_id_card"
                            type="text"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Employee ID Card Details..."}
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Diary Handbook
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.diary_handbook)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="diary_handbook"
                            type="text"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Diary Handbook Details..."}
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Mobile SIM Card
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.mobile_sim_card)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="mobile_sim_card"
                            type="text"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Mobile SIM Card Details..."}
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Access Card
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.access_card)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="access_card"
                            type="text"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Card Details..."}
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Workplace Desk
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.workplace_desk)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="workplace_desk"
                            type="text"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Workplace Desk Details..."}
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">Laptop/PC</label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.laptop_pc)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="laptop_pc"
                            type="text"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Laptop/PC Details..."}
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Visiting Card
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.visiting_cards)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="visiting_cards"
                            type="text"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Visiting Card Details..."}
                          />
                        </>
                      )}
                    </div>

                    {/* Email */}
                    <div>
                      <label className="text-sm text-gray-700">Email</label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.email)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="email"
                            type="email"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder="Enter email address"
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Email ID Password Given
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            initialValues.email_id_password_given === true
                              ? "Yes"
                              : initialValues.email_id_password_given === false
                              ? "No"
                              : null
                          )}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="email_id_password_given"
                            as="select"
                            disabled={isOnboarded && !isHRAdminEditable}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          >
                            <option value="">Select</option>
                            {YES_NO_OPTIONS.map((item, indx) => (
                              <option key={indx} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </Field>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Insurance & Policies */}
                <div>
                  <h3 className="text-lg font-semibold text-primary mb-2 flex items-center gap-2">
                    <span className="inline-block w-1.5 h-5 bg-primary rounded-full" />
                    Insurance & Policies
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-700">
                        Accident Insurance
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.accident_insurance)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="accident_insurance"
                            type="text"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Accident Insurance Details..."}
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Medical Claim Insurance
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(initialValues.mediclaim_insurance)}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="mediclaim_insurance"
                            type="text"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                            placeholder={"Enter Medical Claim Details..."}
                          />
                        </>
                      )}
                    </div>

                    <div>
                      <label className="text-sm text-gray-700">
                        Company Policy Accepted On
                      </label>
                      {isViewMode ? (
                        <p className="mt-1 text-sm">
                          {displayValue(
                            formatDateDDMMYYYY(
                              initialValues.company_policy_accepted_on
                            )
                          )}
                        </p>
                      ) : (
                        <>
                          <Field
                            name="company_policy_accepted_on"
                            type="date"
                            disabled={isOnboarded}
                            className={`${inputClass} disabled:cursor-not-allowed disabled:bg-gray-50`}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons row */}
            <div className="flex justify-end gap-4">
              {!isViewMode && (
                <button
                  type="submit"
                  disabled={loading || (isOnboarded && !isHRAdminEditable)}
                  className="px-4 py-2 bg-primary text-white rounded text-sm hover:bg-secondary disabled:opacity-80 disabled:cursor-not-allowed"
                >
                  {loading ? "Saving..." : "Save Details"}
                </button>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AccountInfoTab;
